/**
 * @fileoverview ListNewsItem component for displaying news items in the top news section
 * @active Component actively used in news deployment
 * @see {@link /src/components/news/topNews/index.jsx} - Parent component that uses this component
 * @see {@link /src/components/news/index.tsx} - Main news detail component
 * @see {@link /src/components/ui/DefaultImage.jsx} - Image component used for news thumbnails
 * @see {@link /src/types/news.ts} - Shared types for news articles
 */

"use client";

import { Flex, Text } from "@chakra-ui/react";
import Link from "next/link";

import DefaultImage from "@/components/ui/DefaultImage";
import { NewsArticle } from "@/types/news";

/**
 * Props for ListNewsItem component
 * @interface ListNewsItemProps
 */
interface ListNewsItemProps {
  /** News item data */
  item: Partial<NewsArticle>;
}

/**
 * ListNewsItem component for rendering individual news items in the top news list
 *
 * @param {ListNewsItemProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const ListNewsItem = ({ item }: ListNewsItemProps): JSX.Element => {
  return (
    <Link href={`/news/${item?.slug}`}>
      <Flex
        p={"12px"}
        cursor={"pointer"}
        alignItems={"start"}
        columnGap={"12px"}
        h={"125px"}
        w={"347px"}
        borderRadius={"7.5px"}
        bg={"white"}
        borderWidth={"0.75px"}
        borderColor={"menuGray"}
      >
        <Flex minW={"80px"} h={"80px"} borderRadius={"8px"} overflow={"hidden"}>
          <DefaultImage
            src={
              item?.newsImageThumbnail
                ? `${process.env.NEXT_PUBLIC_NEWS_IMAGES_PATH || "https://images.aventure.vc/images/news"}/${item?.newsImageThumbnail}`
                : "/images/no-company.svg"
            }
            options={{
              width: "auto",
              height: "80px",
              objectFit: "cover",
              alt: item?.title || "news image",
            }}
          />
        </Flex>

        <Flex flexDir={"column"} rowGap={"4px"}>
          <Text
            fontSize={"14px"}
            fontWeight={"700"}
            letterSpacing={"0.28px"}
            lineHeight={"1.2"}
            noOfLines={3}
          >
            {item?.title}
          </Text>
          <Text
            fontSize={"12px"}
            fontWeight={"400"}
            letterSpacing={"0.48px"}
            lineHeight={"1.35"}
            noOfLines={2}
          >
            {item?.description}
          </Text>
          <Text fontSize={"10px"} fontWeight={"700"} lineHeight={"1"}>
            {item?.publishedDate
              ? new Date(item.publishedDate).toLocaleDateString("en-eu", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : ""}
          </Text>
        </Flex>
      </Flex>
    </Link>
  );
};

export default ListNewsItem;
